import classNames from 'classnames';
import { useCallback, useMemo, useRef } from 'react';
import FlexContainer from '../FlexContainer';
import ContentBlockTitled from '../ContentBlockTitled';
import View from '../View';
import Txt from '../Txt';
import styles from './GraphBars.module.scss';

/**
 * GraphBars
 *
 * @param {String} [className]
 * @param {String} title
 * @param data
 * @param {String[]} labels
 * @param {number} [freeSpace]
 * @return {JSX.Element}
 */
const GraphBars = ({ className, title, data, labels, freeSpace = 0 }) => {
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);

  const { preparedData, maxValue } = useMemo(
    () => GraphBars.prepareData(data),
    [data]
  );

  const maxValueWithSpacing = maxValue + maxValue * freeSpace;

  const handleMouseOver = useCallback(
    (rowData) => (e) => {
      // Set position
      GraphBars.setTooltipPosition(
        tooltipRef.current,
        containerRef.current,
        e.currentTarget
      );

      // Update values
      const values = tooltipRef.current.querySelectorAll('.Txt');
      rowData.data.forEach(
        (v, idx) =>
          (values[idx].innerText = `${data[idx].prefix || ''}${
            data[idx].format ? data[idx].format(v) : v
          }`)
      );
    },
    [data]
  );

  const columnStyle = { flex: `1 1 calc(${100 / labels.length}%)` };

  return (
    <ContentBlockTitled
      ref={containerRef}
      className={classNames(
        styles.GraphBars,
        className,
        `${GraphBars.displayName}`
      )}
      classNameBody={styles.ContentBlockTitledBody}
      rightContent={
        <FlexContainer gap alignCenter className={styles.Legend}>
          {data.map(({ label }) => (
            <FlexContainer key={label} className={styles.LegendRow}>
              <Txt className={styles.LegendText} mm bold>
                {label}
              </Txt>
              <View className={styles.LegendMark} />
            </FlexContainer>
          ))}
        </FlexContainer>
      }
      title={title}
    >
      <FlexContainer className={styles.Body} flex column gap>
        <View className={styles.BodyHeader} />
        <FlexContainer className={styles.BodyBody} gap flex>
          {preparedData.map((i) => (
            <FlexContainer
              key={i.key}
              flex
              onMouseOver={handleMouseOver(i)}
              alignEnd
              className={styles.ValueContainer}
            >
              {i.data.map((v) => (
                <View
                  key={`${i.key}_${v}`}
                  className={styles.Value}
                  style={{
                    height: `calc(${(v / maxValueWithSpacing) * 100}% - 5px)`,
                  }}
                />
              ))}
              <View
                className={styles.TooltipLine}
                style={{
                  height: `${
                    (1 - Math.max(...i.data) / maxValueWithSpacing) * 100
                  }%`,
                }}
              />
            </FlexContainer>
          ))}
        </FlexContainer>
        {labels ? (
          <FlexContainer gap>
            {labels.map((i) => (
              <View key={i} style={columnStyle} className={styles.LabelItem}>
                <Txt mm bold>
                  {i}
                </Txt>
              </View>
            ))}
          </FlexContainer>
        ) : null}
      </FlexContainer>
      <FlexContainer
        className={styles.HoverTooltip}
        column
        ref={tooltipRef}
        gapQuad
      >
        {data.map(({ label }) => (
          <FlexContainer key={label} className={styles.LegendRow} alignCenter>
            <View className={styles.LegendMark} />
            <Txt className={styles.LegendText} mm bold />
          </FlexContainer>
        ))}
      </FlexContainer>
    </ContentBlockTitled>
  );
};

GraphBars.prepareData = (data) =>
  data.reduce(
    (acc, dataSet) => {
      const newPreparedData = [...acc.preparedData];
      let newMaxValue = acc.maxValue;

      dataSet.data.forEach((i, idx) => {
        if (!newPreparedData[idx])
          newPreparedData[idx] = {
            key: idx,
            data: [],
          };

        newPreparedData[idx].data.push(i);
        if (newMaxValue < i) {
          newMaxValue = i;
        }
      });

      return {
        preparedData: newPreparedData,
        maxValue: newMaxValue,
      };
    },
    { preparedData: [], maxValue: 0 }
  );

GraphBars.setTooltipPosition = (tooltipEl, parentEl, targetEl) => {
  const targetBound = targetEl.getBoundingClientRect();
  const parentBound = parentEl.getBoundingClientRect();

  const relativePos = {};

  relativePos.top = targetBound.top - parentBound.top;
  relativePos.right = targetBound.right - parentBound.right;
  relativePos.bottom = targetBound.bottom - parentBound.bottom;
  relativePos.left = targetBound.left - parentBound.left;

  tooltipEl.classList.add(styles.HoverTooltip__active);
  const tooltipElBound = tooltipEl.getBoundingClientRect();
  const calculatedPosition = {
    left: relativePos.left - tooltipElBound.width / 2 + targetBound.width / 2,
    top: relativePos.top - tooltipElBound.height - 5,
  };

  const absoluteLeft = parentBound.left + calculatedPosition.left;
  const gap = 5;
  if (absoluteLeft < gap) {
    calculatedPosition.left = gap;
  } else {
    const absoluteLeftWidth = absoluteLeft + tooltipElBound.width;
    const diffLeft =
      (window.innerWidth || document.documentElement.clientWidth) -
      absoluteLeftWidth;
    if (diffLeft < gap) {
      calculatedPosition.left += diffLeft - gap;
    }
  }

  tooltipEl.style.left = `${calculatedPosition.left}px`;
  tooltipEl.style.top = `${calculatedPosition.top}px`;
};

GraphBars.displayName = 'GraphBars';

export default GraphBars;
